import { useEffect, useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import { AppLayout } from ".";
import { useAdStudioRole } from "@/Hooks/useAdStudioRole";
import { ConfettiOverlay } from "@/Components/ui/ConfettiOverlay";
import { confettiEmitter } from "@/Utilities/confettiEmitter";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { UnstyledButton, Text } from "@mantine/core";
import { IconAd, IconCompass, IconUser, IconUsers } from "@tabler/icons-react";
import classes from "./StudioLayout.module.css";

export default function ({ children, pageTitle = null, withBottomNav = true }) {
  const { t } = useTranslation();
  // auth is null on the public changelog page (AD-6), so this cannot destructure
  // straight through it.
  const { auth, isShowcase = false, flash } = usePage().props;
  const { user } = auth ?? {};
  const { isAdmin, isEditor } = useAdStudioRole();
  const [showConfetti, setShowConfetti] = useState(false);
  const seesEditorTools = [1, 6].includes(Number(user?.type));
  // AD-230: on a phone the burger only adds the pages the bottom bar has no room for.
  const hasBottomNav = withBottomNav && !!user;

  useEffect(() => {
    const unsubscribe = confettiEmitter.subscribe(() => {
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 1500);
    });

    return () => unsubscribe();
  }, []);

  // A message a redirect carried here (session flash), e.g. why an email link no
  // longer works (AD-225).
  useEffect(() => {
    // An id per message: Back/Forward restores the props, and the same message is
    // then not shown a second time while the first is still up.
    if (flash?.error) notifications.show({ id: `flash-error-${flash.error}`, color: "red", message: flash.error });
    if (flash?.success) notifications.show({ id: `flash-success-${flash.success}`, color: "green", message: flash.success });
    if (flash?.info) notifications.show({ id: `flash-info-${flash.info}`, color: "blue", message: flash.info });
  }, [flash?.error, flash?.success, flash?.info]);

  return (
    <>
      <AppLayout
        pageTitle={pageTitle}
        headerProps={{
          logoProps: {
            type: 'studio',
            link: {
              component: Link,
              href: route('studio.home')
            }
          },
          menuItems: [
            {
              name: t('nav.inspiration'),
              url: route('studio.home'),
              isReact: true,
              active: route().current('studio.home') || route().current('studio.templates'),
            },
            {
              name: isAdmin ? t('nav.ads') : t('nav.myAds'),
              url: route('studio.ad_builder.index'),
              isReact: true,
              active: route().current(isShowcase ? 'studio.showcase.*' : 'studio.ad_builder.*'),
            },
            ...(seesEditorTools ? [{
              name: 'Template Reviews',
              url: route('studio.studio_editor_v2.template_reviews.index'),
              isReact: true,
              active: route().current('studio.studio_editor_v2.template_reviews.*'),
            }] : []),
            ...(seesEditorTools ? [{
              name: 'AI Schemas',
              url: route('studio.studio_editor_v2.ai_schemas.index'),
              isReact: true,
              active: route().current('studio.studio_editor_v2.ai_schemas.*'),
            }, {
              name: 'MCP Guide',
              url: route('studio.studio_editor_v2.mcp_docs'),
              isReact: true,
              active: route().current('studio.studio_editor_v2.mcp_docs'),
            }] : []),
            ...(user && !isAdmin && !isEditor ? [{
              name: t('nav.users'),
              url: route('studio.user.index'),
              isReact: true,
              active: route().current('studio.user.*'),
            }] : []),
            // AD-222: an Editor (designer) has Tools with Design Requests only.
            ...(isEditor ? [{
              name: t('nav.tools'),
              items: [
                {
                  name: t('nav.designRequests'),
                  url: route('studio.design_request.index'),
                  isReact: true,
                  active: route().current('studio.design_request.*'),
                },
              ],
            }] : []),
            ...(isAdmin ? [{
              name: t('nav.tools'),
              items: [
                // AD-222: the designers' "Let Us Design It" list.
                {
                  name: t('nav.designRequests'),
                  url: route('studio.design_request.index'),
                  isReact: true,
                  active: route().current('studio.design_request.*'),
                },
                {
                  name: t('nav.demos'),
                  url: route('studio.demo.index'),
                  isReact: true,
                  active: route().current('studio.demo.*'),
                },
                {
                  name: 'Integrations',
                  url: route('studio.integration.index'),
                  isReact: true,
                  active: route().current('studio.integration.*'),
                },
                {
                  name: 'Packages',
                  name: t('nav.packages'),
                  url: route('studio.package.index'),
                  isReact: true,
                  active: route().current('studio.package.*'),
                },
                {
                  name: t('nav.users'),
                  url: route('studio.user.index'),
                  isReact: true,
                  active: route().current('studio.user.*'),
                },
                {
                  name: 'Changelog',
                  url: route('studio.changelog_entry.index'),
                  isReact: true,
                  active: route().current('studio.changelog_entry.*'),
                },
              ],
            }] : []),
          ],
          profileUrl: route('studio.profile'),
          logoutUrl: route('studio.logout'),
          withWhatsNew: true,
          withBurger: !user || (hasBottomNav && (isAdmin || isEditor || seesEditorTools)),
          withPhoneUserMenu: false,
        }}
        bottomNav={hasBottomNav && (
          <nav className={classes.bottomNav}>
            {[
              { name: t('nav.inspiration'), icon: IconCompass, url: route('studio.home') },
              { name: isAdmin ? t('nav.ads') : t('nav.myAds'), icon: IconAd, url: route('studio.ad_builder.index') },
              ...(isEditor ? [] : [{ name: t('nav.users'), icon: IconUsers, url: route('studio.user.index') }]),
              { name: t('nav.account'), icon: IconUser, url: route('studio.profile') },
            ].map(({ name, icon: Icon, url }) => (
              <UnstyledButton key={url} component={Link} href={url} className={classes.bottomNavItem}>
                <Icon size={20} />
                <Text span fz={12} lh={1.55}>{name}</Text>
              </UnstyledButton>
            ))}
          </nav>
        )}>
        {children}
      </AppLayout>

      <ConfettiOverlay show={showConfetti} />
    </>
  );
}
